import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { loginUser } from "../../redux/Slice/Auth/authSlice";
import { useNavigate, useSearchParams } from "react-router-dom";
// claimPendingShopifyConnection no longer called here — see goToShopifyWorkspacePicker below.
// import { claimPendingShopifyConnection } from "../../services/shopifyApi";
import OTPDialog from "../../components/Auth/OTPDialog";
import {
  Box,
  Button,
  TextField,
  Typography,
  Container,
  InputAdornment,
  IconButton,
  CircularProgress,
} from "@mui/material";
import ClusterDenLogo from "../../Images/Login/ClusterDenLogo.png";
import ClusterDenLogoWhite from "../../Images/Login/ClusterDenLogoWhite.png";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import { useThemeContext } from "../../contexts/ThemeContext";
import { TilesBackground } from "../../components/Auth/TilesBackground";
import { LoginFeatureCarousel } from "../../components/Auth/LoginFeatureCarousel";

// The branding panel's margin/padding are fixed px regardless of viewport height, so on a
// short window (small laptop) they eat a much bigger share of the space, squeezing the
// carousel image down disproportionately. Shrink the fixed chrome on short viewports so the
// image gets to keep most of the available room instead of compounding the squeeze.
const AUTH_COMPACT_HEIGHT_MQ = "@media (max-height:800px)";

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showOTPDialog, setShowOTPDialog] = useState(false);
  const [otpEmail, setOtpEmail] = useState("");

  const { mode } = useThemeContext();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isLoading, isError, errorMessage } = useSelector((state) => state.auth);
  // Carried from Welcome.jsx's "Already have a ClusterDen account? Log in instead" link
  // (or Signup.jsx's own Login link) — a merchant whose existing session wasn't
  // auto-detected during a Shopify install (different browser/device, expired session)
  // can still attach that pending connection to their account instead of accidentally
  // creating a duplicate new one. See shopifyApi.js's claimPendingShopifyConnection.
  const [searchParams] = useSearchParams();
  const shop = searchParams.get("shop") || "";

  // REDESIGN 2026-10-16: no longer claims immediately with whatever workspace the login
  // response carries. Login.jsx unmounts the instant login succeeds (App.js's "/" route
  // swaps to the authenticated home), so an in-page picker can't survive here — instead
  // hand off to /welcome?shop=..., which already shows the workspace picker for a
  // logged-in merchant and then calls claim-pending with the chosen workspace.
  // Returns true when it took over navigation.
  const goToShopifyWorkspacePicker = () => {
    if (!shop) return false;
    navigate(`/welcome?shop=${encodeURIComponent(shop)}`);
    return true;
  };

  // Superseded by goToShopifyWorkspacePicker. Commented out, not deleted.
  // const claimPendingShopifyIfNeeded = async (accountId, workspaceId, accessToken) => {
  //   if (!shop || !workspaceId || !accessToken) return;
  //   try {
  //     await claimPendingShopifyConnection(shop, workspaceId, accessToken);
  //   } catch (err) {
  //     console.warn("Could not claim pending Shopify connection:", err.message);
  //   }
  // };

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const result = await dispatch(loginUser({ email, password }));

      if (result.meta.requestStatus === "fulfilled") {
        // Check if 2FA is required
        if (result.payload.requires2FA) {
          setOtpEmail(result.payload.email || email);
          setShowOTPDialog(true);
          return;
        }

        if (goToShopifyWorkspacePicker()) return;

        // Someone who hasn't finished onboarding gets sent back there on
        // every login (not just right after email verification), so an
        // abandoned /setup never silently lands them in the dashboard.
        if (result.payload.requiresOnboarding) {
          navigate("/setup");
          return;
        }

        // Normal login flow
        const userId = result.payload.accountId;
        navigate(`/home/${userId}`);
      } else {
        navigate("/");
      }
    } catch (error) {
      navigate("/");
    }
  };

  const handleOTPSuccess = async (result) => {
    if (goToShopifyWorkspacePicker()) return;
    // OTP verification successful, navigate to home
    const userId = result.accountId;
    navigate(`/home/${userId}`);
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const isLoginDisabled = isLoading || !email.trim() || !password.trim();

  // Google Sign-In handler - accepts both credentialResponse and direct response


  // Ref for GoogleLogin component

  // Handle custom button click - trigger Google Sign-In by clicking the hidden GoogleLogin button


  // Enhanced theme-aware field styling with proper state management
  const getFieldStyles = () => {
    if (mode === "dark") {
      return {
        "& .MuiOutlinedInput-root": {
          backgroundColor: "#141414",
          borderRadius: "12px",
          transition: "all 0.3s ease",
          "& fieldset": { borderColor: "#333333", borderWidth: "1.5px" },
          "&:hover": {
            backgroundColor: "#1c1c1c",
            "& fieldset": { borderColor: "#4a4a4a" },
          },
          "&.Mui-focused": {
            backgroundColor: "#1c1c1c",
            "& fieldset": { borderColor: "#34d399", borderWidth: "2px" },
          },
          "&.Mui-error": {
            "& fieldset": { borderColor: "#ef4444" },
          },
        },
        "& .MuiInputBase-input": {
          color: "#f5f5f5",
          fontFamily: "Instrument Sans, sans-serif",
          fontSize: "14.5px",
          padding: "11px 14px",
          "&:-webkit-autofill": {
            WebkitBoxShadow: "0 0 0 100px #141414 inset !important",
            WebkitTextFillColor: "#f5f5f5 !important",
            caretColor: "#f5f5f5",
            transition: "background-color 5000s ease-in-out 0s",
          },
          "&:-webkit-autofill:hover": { WebkitBoxShadow: "0 0 0 100px #1c1c1c inset !important" },
          "&:-webkit-autofill:focus": { WebkitBoxShadow: "0 0 0 100px #1c1c1c inset !important" },
          "&::selection": { backgroundColor: "rgba(79,195,137,0.4)", color: "#ffffff" },
        },
        "& .MuiSvgIcon-root": { color: "#6b7280" },
        "& .MuiFormHelperText-root": {
          color: "#9ca3af",
          "&.Mui-error": { color: "#ef4444" },
        },
      };
    }
    return {
      "& .MuiOutlinedInput-root": {
        backgroundColor: "#F3F4F6",
        borderRadius: "12px",
        "& fieldset": { borderColor: "#E5E7EB" },
        "&:hover fieldset": { borderColor: "#D1D5DB" },
        "&.Mui-focused fieldset": { borderColor: "#a0eaa3", borderWidth: "2px" },
        "&.Mui-error fieldset": { borderColor: "#f44336" },
      },
      "& .MuiInputBase-input": {
        color: "#111827",
        fontFamily: "Instrument Sans, sans-serif",
        fontSize: "17px",
        padding: "16px 16px",
        "&:-webkit-autofill": {
          WebkitBoxShadow: "0 0 0 100px #F3F4F6 inset !important",
          WebkitTextFillColor: "#111827 !important",
          caretColor: "#111827",
          transition: "background-color 5000s ease-in-out 0s",
        },
        "&:-webkit-autofill:hover": { WebkitBoxShadow: "0 0 0 100px #F3F4F6 inset !important" },
        "&:-webkit-autofill:focus": { WebkitBoxShadow: "0 0 0 100px #F3F4F6 inset !important" },
        "&::selection": { backgroundColor: "rgba(68,181,124,0.3)", color: "#111827" },
      },
    };
  };

  const accentColor = mode === "dark" ? "#4FC389" : "#44B57C";

  const fieldLabelSx = {
    mb: 0.75,
    fontWeight: 600,
    fontSize: "15px",
    color: mode === "dark" ? "#d1d5db" : "#374151",
    fontFamily: "Instrument Sans, sans-serif",
  };
  const stepHeadingSx = {
    mb: 0,
    lineHeight: 1.2,
    fontWeight: 700,
    fontSize: { xs: "1.35rem", sm: "1.5rem" },
    color: mode === "dark" ? "#f5f5f5" : "#121a34",
    fontFamily: "Instrument Sans, sans-serif",
    textAlign: "center",
    [AUTH_COMPACT_HEIGHT_MQ]: { fontSize: "1.2rem" },
  };
  const stepSubheadingSx = {
    mt: 0.5,
    mb: 2.25,
    fontSize: "15px",
    color: mode === "dark" ? "#9ca3af" : "#6b7280",
    fontFamily: "Instrument Sans, sans-serif",
    textAlign: "center",
    [AUTH_COMPACT_HEIGHT_MQ]: { mb: 1.25 },
  };

  return (
    <Box
      sx={{
        height: "100dvh",
        overflow: "hidden",
        background: mode === "dark" ? "#121212" : "#FAFBFC",
        fontFamily: "Instrument Sans, sans-serif",
        transition: "background-color 0.3s ease",
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        alignItems: "stretch",
        // Caps the two-panel layout on genuinely ultrawide monitors only. MUI's "xl"
        // breakpoint (1536px) is too low a threshold here — 1920px is an extremely common
        // standard desktop resolution, not ultrawide, and was wrongly getting capped/centered
        // by this rule. Use an explicit high min-width instead of the xl breakpoint.
        maxWidth: "none",
        "@media (min-width:2200px)": { maxWidth: "1800px" },
        mx: "auto",
      }}
    >
      {/* Left branding card — standalone, floats with margin, not attached to the form */}
      <Box
        sx={{
          display: { xs: "none", md: "flex" },
          flex: "0 0 39%",
          flexDirection: "column",
          m: { md: 3 },
          p: { md: 5 },
          pl: { md: 5, lg: 5.5 },
          borderRadius: 5,
          overflow: "hidden",
          backgroundColor: mode === "dark" ? "#1E1E1E" : "#FAFBFC",
          border: mode === "dark" ? "none" : "2px solid rgba(0,0,0,0.1)",
          color: mode === "dark" ? "#f5f5f5" : "#000000",
          position: "relative",
          [AUTH_COMPACT_HEIGHT_MQ]: { m: 1.5, p: 3, pl: { md: 4, lg: 4.5 } },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 5,
            [AUTH_COMPACT_HEIGHT_MQ]: { mb: 2 },
          }}
        >
          <Box
            component="img"
            src={mode === "dark" ? ClusterDenLogoWhite : ClusterDenLogo}
            alt="ClusterDen"
            sx={{
              height: 44,
              width: "auto",
              maxWidth: 200,
              objectFit: "contain",
            }}
          />
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Button
              onClick={() => window.open("https://wa.me/923250909111", "_blank", "noopener,noreferrer")}
              sx={{
                p: 0,
                minWidth: "auto",
                fontSize: "13px",
                fontWeight: 500,
                textTransform: "none",
                color: mode === "dark" ? "#9ca3af" : "#6B7280",
                fontFamily: "Instrument Sans, sans-serif",
                "&:hover": { color: mode === "dark" ? "#ffffff" : "#000000", backgroundColor: "transparent" },
              }}
            >
              Help
            </Button>
            <Typography sx={{ fontSize: "13px", color: mode === "dark" ? "#3f3f3f" : "#D1D5DB" }}>·</Typography>
            <Button
              onClick={() => window.open("https://clusterden.com/privacy", "_blank", "noopener,noreferrer")}
              sx={{
                p: 0,
                minWidth: "auto",
                fontSize: "13px",
                fontWeight: 500,
                textTransform: "none",
                color: mode === "dark" ? "#9ca3af" : "#6B7280",
                fontFamily: "Instrument Sans, sans-serif",
                "&:hover": { color: mode === "dark" ? "#ffffff" : "#000000", backgroundColor: "transparent" },
              }}
            >
              Privacy
            </Button>
            <Typography sx={{ fontSize: "13px", color: mode === "dark" ? "#3f3f3f" : "#D1D5DB" }}>·</Typography>
            <Button
              onClick={() => window.open("https://clusterden.com/terms", "_blank", "noopener,noreferrer")}
              sx={{
                p: 0,
                minWidth: "auto",
                fontSize: "13px",
                fontWeight: 500,
                textTransform: "none",
                color: mode === "dark" ? "#9ca3af" : "#6B7280",
                fontFamily: "Instrument Sans, sans-serif",
                "&:hover": { color: mode === "dark" ? "#ffffff" : "#000000", backgroundColor: "transparent" },
              }}
            >
              Terms
            </Button>
          </Box>
        </Box>

        {/* Headline */}
        <Typography
          component="h1"
          sx={{
            fontWeight: 500,
            fontSize: { md: "1.95rem", lg: "2.25rem" },
            lineHeight: 1.25,
            letterSpacing: "-0.015em",
            mb: 1,
            whiteSpace: "nowrap",
            color: mode === "dark" ? "#f5f5f5" : "#000000",
          }}
        >
          Welcome back <span role="img" aria-label="waving hand">👋</span>
        </Typography>
        <Typography
          sx={{
            fontSize: { md: "0.95rem", lg: "1.05rem" },
            lineHeight: 1.6,
            color: "#9CA3AF",
            whiteSpace: "nowrap",
            mb: 2,
          }}
        >
          Here&apos;s what&apos;s new since you last logged in.
        </Typography>

        <LoginFeatureCarousel mode={mode} />
      </Box>

      {/* Right side — animated tile background with the form as a floating card on top */}
      <Box
        sx={{
          flex: 1,
          position: "relative",
          display: "flex",
          flexDirection: "column",
          // "safe center" instead of plain "center": when the card grows taller than the
          // viewport (e.g. the password-requirements checklist appearing), plain "center"
          // pushes half the overflow ABOVE the visible area, which is then permanently
          // unreachable (a centered flex container can't scroll to negative space) — only
          // the downward half could ever be scrolled to. "safe center" falls back to
          // top-alignment once content overflows, keeping everything scrollable/reachable.
          justifyContent: "safe center",
          boxSizing: "border-box",
          padding: { xs: 2.5, sm: 3, md: 4 },
          overflowY: "auto",
          overflowX: "hidden",
          backgroundColor: mode === "dark" ? "#121212" : "#FAFBFC",
        }}
      >
        <TilesBackground
          borderColor={mode === "dark" ? "rgba(255,255,255,0.09)" : "rgba(17,24,39,0.05)"}
          hoverColor={mode === "dark" ? "rgba(79,195,137,0.16)" : "rgba(68,181,124,0.12)"}
        />
        <Container maxWidth="sm" sx={{ position: "relative", zIndex: 1, boxSizing: "border-box" }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              width: "100%",
              maxWidth: 520,
              boxSizing: "border-box",
              mx: "auto",
              py: { xs: 3.5, sm: 4.5, md: 5 },
              px: { xs: 3, sm: 4.5, md: 5 },
              [AUTH_COMPACT_HEIGHT_MQ]: { py: 2 },
              borderRadius: 4,
              backgroundColor: mode === "dark" ? "#1E1E1E" : "#FFFFFF",
              border: mode === "dark" ? "2px solid rgba(255,255,255,0.12)" : "none",
              boxShadow:
                mode === "dark"
                  ? "0 2px 10px rgba(0,0,0,0.45), 0 30px 80px rgba(0,0,0,0.7)"
                  : "0 2px 10px rgba(17,24,39,0.08), 0 30px 70px rgba(17,24,39,0.18)",
            }}
          >
            <Box component="form" noValidate onSubmit={handleLogin}>
              <Typography variant="h6" sx={stepHeadingSx}>
                Log in
              </Typography>
              <Typography sx={stepSubheadingSx}>
                Please enter your credentials to continue.
              </Typography>

              <Typography sx={fieldLabelSx}>Email Address</Typography>

              <TextField
                fullWidth
                name="email"
                placeholder="your@email.com"
                type="email"
                autoComplete="email"
                spellCheck={false}
                data-gramm="false"
                data-gramm_editor="false"
                data-enable-grammarly="false"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailOutlinedIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 1.75, [AUTH_COMPACT_HEIGHT_MQ]: { mb: 1 }, ...getFieldStyles() }}
              />

              <Typography sx={fieldLabelSx}>Password</Typography>

              <TextField
                fullWidth
                name="password"
                type={showPassword ? "text" : "password"}
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockOutlinedIcon />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleClickShowPassword}
                        edge="end"
                        sx={{
                          color: mode === "dark" ? "#757575" : "#9CA3AF",
                          "&:hover": { color: mode === "dark" ? "#9E9E9E" : "#6B7280", backgroundColor: "transparent" },
                        }}
                      >
                        {showPassword ? <VisibilityOffOutlinedIcon /> : <VisibilityOutlinedIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 1, [AUTH_COMPACT_HEIGHT_MQ]: { mb: 0.75 }, ...getFieldStyles() }}
              />

              {/* Forgot Password Link */}
              <Box sx={{ textAlign: "right", mb: 2.5, [AUTH_COMPACT_HEIGHT_MQ]: { mb: 1.5 } }}>
                <Button
                  onClick={() => navigate("/forgot-password")}
                  sx={{
                    color: mode === "dark" ? "#10b981" : "#2D9B39",
                    textTransform: "none",
                    fontWeight: 500,
                    fontSize: "14px",
                    p: 0,
                    minWidth: "auto",
                    fontFamily: "Instrument Sans, sans-serif",
                    "&:hover": {
                      backgroundColor: "transparent",
                      textDecoration: "underline",
                      color: mode === "dark" ? "#059669" : "#1a7f64",
                    },
                  }}
                >
                  Forgot password?
                </Button>
              </Box>

              {isError && (
                <Box
                  sx={{
                    mb: 2.5,
                    p: 2,
                    backgroundColor: mode === "dark" ? "rgba(244, 67, 54, 0.1)" : "rgba(244, 67, 54, 0.08)",
                    borderRadius: "12px",
                    border: `1px solid ${mode === "dark" ? "rgba(244, 67, 54, 0.3)" : "rgba(244, 67, 54, 0.2)"}`,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: mode === "dark" ? "#f88078" : "#d32f2f",
                      fontFamily: "Instrument Sans, sans-serif",
                      fontSize: "14px",
                      lineHeight: 1.5,
                    }}
                  >
                    {errorMessage}
                  </Typography>
                </Box>
              )}

              <Button
                disabled={isLoginDisabled}
                type="submit"
                fullWidth
                variant="contained"
                sx={{
                  py: 1.25,
                  mt: 0.5,
                  [AUTH_COMPACT_HEIGHT_MQ]: { py: 1, mt: 0.25 },
                  backgroundColor: accentColor,
                  color: "#ffffff",
                  borderRadius: "12px",
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "1rem",
                  fontFamily: "Instrument Sans, sans-serif",
                  boxShadow:
                    mode === "dark"
                      ? "none"
                      : "0 2px 8px rgba(68,181,124,0.25)",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    backgroundColor: mode === "dark" ? "#3aad77" : "#38a06c",
                    boxShadow:
                      mode === "dark"
                        ? "none"
                        : "0 8px 20px rgba(68,181,124,0.35)",
                  },
                  "&.Mui-disabled": {
                    backgroundColor: mode === "dark" ? "#2a2a2a" : "#c8e6ca",
                    color: mode === "dark" ? "#4b5563" : "#6d8a6f",
                  },
                }}
              >
                {isLoading ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2, color: "inherit" }}>
                    <span>Logging in</span>
                    <CircularProgress size={20} thickness={5} sx={{ color: "#ffffff" }} />
                  </Box>
                ) : (
                  "Login"
                )}
              </Button>
            </Box>

            <Box sx={{ textAlign: "center", mt: 2, [AUTH_COMPACT_HEIGHT_MQ]: { mt: 1 } }}>
              <Typography
                variant="body2"
                sx={{
                  fontFamily: "Instrument Sans, sans-serif",
                  color: mode === "dark" ? "#A1A1AA" : "#6B7280",
                  fontSize: "14px",
                  display: "inline",
                }}
              >
                Don&apos;t have an account?{" "}
                <Button
                  onClick={() => navigate("/signup")}
                  sx={{
                    color: mode === "dark" ? "#10b981" : "#2D9B39",
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "14px",
                    p: 0,
                    minWidth: "auto",
                    fontFamily: "Instrument Sans, sans-serif",
                    verticalAlign: "baseline",
                    "&:hover": {
                      backgroundColor: "transparent",
                      color: mode === "dark" ? "#059669" : "#1a7f64",
                    },
                  }}
                >
                  Sign up
                </Button>
              </Typography>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* OTP Dialog for 2FA */}
      <OTPDialog
        open={showOTPDialog}
        onClose={() => {
          setShowOTPDialog(false);
          setOtpEmail("");
        }}
        email={otpEmail}
        onSuccess={handleOTPSuccess}
      />
    </Box>
  );
};

export default Login;
